import { defaultNavigationConfig } from '@/configs/navigationConfig';
import { getFilteredNavigation } from '@/configs/navigationConfigUtils';
import { useConfig } from '@/utils/useConfig';
import { ConfigValues } from '@/utils/configManager';
import useUser from '@auth/useUser';
import { useMemo } from 'react';

/**
 * Hook to manage navigation configuration with reactive config updates
 * This hook:
 * - Only fetches configs when enabled (after authentication is determined)
 * - Loads all navigation-related configs using useConfig
 * - Filters navigation based on config values
 * - Automatically updates when configs change
 * - Provides default navigation structure
 * 
 * @param enabled - Whether to fetch configs (should be true only when authenticated and auth state is determined)
 */
export const useNavigationConfig = (enabled = false) => {
    const { data: user } = useUser();
    const userRoles = user?.roles;

    // Load all navigation-related configs - only when enabled
    const accountConfig = useConfig('account_config', enabled);
    const misConfig = useConfig('mis_config', enabled);
    const billingConfig = useConfig('billing_config', enabled);
    const webrarConfig = useConfig('webrar_config', enabled);
    const zoneEditorConfig = useConfig('zone_editor_config', enabled);
    const onboardingConfig = useConfig('onboarding_config', enabled);
    const navbarConfig = useConfig('navbar_config', enabled);

    // Memoize configs object to prevent unnecessary rerenders
    const configs = useMemo<Record<string, ConfigValues>>(() => ({
        'account_config': accountConfig.config,
        'mis_config': misConfig.config,
        'billing_config': billingConfig.config,
        'webrar_config': webrarConfig.config,
        'onboarding_config': onboardingConfig.config,
        'navbar_config': navbarConfig.config,
    }), [
        accountConfig.config,
        misConfig.config,
        billingConfig.config,
        webrarConfig.config,
        onboardingConfig.config,
        navbarConfig.config,
    ]);

    // Calculate if any config is still loading
    const isLoading = accountConfig.loading || misConfig.loading || billingConfig.loading || webrarConfig.loading || zoneEditorConfig.loading || onboardingConfig.loading || navbarConfig.loading;

    // Calculate if any config has an error
    const hasError = accountConfig.error !== null || misConfig.error !== null || billingConfig.error !== null || webrarConfig.error !== null || zoneEditorConfig.error !== null || onboardingConfig.error !== null || navbarConfig.error !== null;    // Filter navigation based on loaded configs
    const filteredNavigation = useMemo(() => {
        return getFilteredNavigation(configs, defaultNavigationConfig, userRoles);
    }, [configs, userRoles]);

    return {
        navigation: filteredNavigation,
        loading: isLoading,
        error: hasError ? 'Failed to load some navigation configurations' : null,
        configs
    };
};
